import { Fragment } from 'react';
import { Alert } from '@mui/material';
import type { UseFormSetError } from 'react-hook-form';

import type { FormSlots, LeaseDetailsFormConfig } from '@/features/LeaseDetails';
import type { AddressAutocompleteOption } from '@/utils/types/addressAutocompleteOption.interface';
import type { Renter } from '@/utils/types/renter.interface';

import { CoverageRequirementsAlert } from './content/invite-lease-details.drawer.coverage-requirements-alert';
import { InviteLeaseDetailsFormDivider } from './components/invite-lease-details-form-divider';
import { InviteLeaseDetailsEffects } from './invite-lease-details.effects';
import { InviteLeaseDetailsSegmentEvents } from './constants';
import {
  addressValidate,
  apartmentNumberValidate,
  applicantEmailValidate,
  applicantsUniqueValidate,
  grossMonthlyRentValidate,
  leaseEndValidate,
  leaseStartValidate,
  moneyValidate,
} from './invite-lease-details.field-schemas';
import { INVITE_LEASE_DETAILS_VALIDATION_MESSAGES as MSG } from './constants';
import type { InviteLeaseDetailsFormValues } from './invite-lease-details.form-values';
import type { InviteLeaseDetailsPresetDeps } from './invite-lease-details.preset-deps';
import {
  buildInviteCoverageSection,
  buildInviteScreeningSection,
} from './invite-lease-details.sections';
import { InviteLeaseDetailsSharedFooter } from './invite-lease-details.shared-footer';
import { getApplicantEmailValidationResult, mapInviteFormValuesToRenter } from './utils';

// The single place that knows about invite's variant flags (SMB / PMI / new
// coverages / customizable coverage). It maps `(user, flags)` → a config the
// shared engine renders; the engine itself stays variant-blind. Submit reuses
// the existing controller pipeline by mapping engine values back to `Renter`.

export type { InviteLeaseDetailsPresetDeps } from './invite-lease-details.preset-deps';

/**
 * The submit pipeline (`applyInviteServiceFieldErrors`, the controller's
 * applicant error fan-out) is shared with the legacy form, which keys
 * applicants under `applicantEmails.X.value`. The shared engine stores them
 * under `applicants.X.email`. This is the one diverging path; every other
 * field name in `API_TO_FORM_FIELD` already uses engine-matching names, so
 * they need no translation.
 */
export const legacyApplicantPathToEngine = (path: string): string => {
  const match = /^applicantEmails\.(\d+)\.value$/.exec(path);
  return match ? `applicants.${match[1]}.email` : path;
};

const LEASE_INFORMATION_SUBTITLE =
  "Enter the lease details below. If your applicant is paying for the entire first month upfront at lease signing, we don't need to cover it (optional).";

export const inviteLeaseDetailsPreset = (
  deps: InviteLeaseDetailsPresetDeps,
): LeaseDetailsFormConfig => {
  const {
    isSmbInvite,
    isPmi,
    newCoveragesEnabled,
    customCoverageDisabled,
    errorMsg,
    trackInvite,
    onMilestoneReached,
    onSelfEnrolledChange,
    selectedSmbPricingRef,
    onSubmit,
  } = deps;

  const slots: FormSlots = {
    top: (
      <Fragment>
        <InviteLeaseDetailsEffects
          isSmbInvite={isSmbInvite}
          newCoveragesEnabled={newCoveragesEnabled}
          onSelfEnrolledChange={onSelfEnrolledChange}
          selectedSmbPricingRef={selectedSmbPricingRef}
        />
        {customCoverageDisabled ? <CoverageRequirementsAlert /> : null}
      </Fragment>
    ),
    bottom: (
      <Fragment>
        {errorMsg ? (
          <Alert severity="error" sx={{ marginY: 2 }}>
            {errorMsg}
          </Alert>
        ) : null}
        <InviteLeaseDetailsSharedFooter />
      </Fragment>
    ),
  };

  return {
    sections: {
      leaseInformation: {
        visible: true,
        title: 'Lease information',
        subtitle: LEASE_INFORMATION_SUBTITLE,
        fields: {
          address: {
            kind: 'address',
            visible: true,
            required: true,
            label: 'Search by address or property name',
            validate: addressValidate,
            events: {
              onChange: ({ value }) => {
                const option = value as AddressAutocompleteOption | null;
                if (option?.monolith_building_id) {
                  onMilestoneReached?.('hadPropertySelected');
                  trackInvite(InviteLeaseDetailsSegmentEvents.PropertySelected, {
                    property_id: option.property_id,
                  });
                }
              },
            },
          },
          unit: {
            kind: 'text',
            visible: true,
            required: true,
            requiredMessage: MSG.UNIT_NUMBER_REQUIRED,
            label: 'Unit number',
            validate: apartmentNumberValidate,
          },
          leaseStartDate: {
            kind: 'date',
            visible: true,
            required: true,
            requiredMessage: MSG.LEASE_START_REQUIRED,
            invalidDateMessage: MSG.LEASE_START_INVALID,
            rowGroup: 'lease-dates',
            label: 'Lease start date',
            minDateOffsetDays: 365,
            // Mirrors `leaseStartValidate`'s `max-one-year-future`: ~1y forward.
            // The validator still owns the precise edge (- 1 day); this is the
            // UI hint, restoring the legacy picker's maxDate behavior.
            maxDateOffsetDays: 365,
            validate: leaseStartValidate(),
          },
          leaseEndDate: {
            kind: 'date',
            visible: true,
            required: true,
            requiredMessage: MSG.LEASE_END_REQUIRED,
            invalidDateMessage: MSG.LEASE_END_INVALID,
            rowGroup: 'lease-dates',
            label: 'Lease end date',
            minDateOffsetDays: 365,
            // Mirrors `leaseEndValidate`'s `max-eleven-years-future`: ~11y forward.
            maxDateOffsetDays: 365 * 11,
            validate: leaseEndValidate(newCoveragesEnabled),
          },
          grossMonthlyRent: {
            kind: 'money',
            visible: true,
            required: true,
            requiredMessage: MSG.TOTAL_MONTHLY_RENT_REQUIRED,
            label: 'Total monthly rent',
            helperText: 'This is the market price rent that is on the lease.',
            validate: grossMonthlyRentValidate,
          },
          prepaidRent: {
            kind: 'money',
            visible: true,
            required: false,
            rowGroup: 'rent-extras',
            label: 'Prepaid rent',
            helperText: 'Any rent that will be collected at lease signing.',
            fixedDecimalScale: true,
            validate: moneyValidate,
          },
          freeRent: {
            kind: 'money',
            visible: true,
            required: false,
            rowGroup: 'rent-extras',
            label: 'Free rent',
            helperText:
              'Any concessions offered to the renter where they are not responsible for making rent payments.',
            fixedDecimalScale: true,
            validate: moneyValidate,
          },
        },
      },
      applicants: {
        visible: true,
        title: 'Applicant contact information',
        subtitle:
          "Enter your applicant's email address below. You can add up to five additional applicants, if applicable.",
        min: 1,
        max: 6,
        primaryLocked: false,
        addButtonLabel: 'Add additional applicant',
        compactEmailLabels: {
          primary: 'Primary Applicant Email Address',
          additional: 'Additional Applicant Email Address',
        },
        arrayValidate: applicantsUniqueValidate,
        onAdd: ({ count, index }) =>
          trackInvite(InviteLeaseDetailsSegmentEvents.AdditionalApplicantChanged, {
            action: 'add',
            applicant_count: count,
            applicant_index: index,
          }),
        onRemove: ({ count, index }) =>
          trackInvite(InviteLeaseDetailsSegmentEvents.AdditionalApplicantChanged, {
            action: 'remove',
            applicant_count: count,
            applicant_index: index,
          }),
        fields: {
          email: {
            kind: 'text',
            visible: true,
            required: true,
            requiredMessage: MSG.EMAIL_REQUIRED,
            defaultValue: '',
            label: 'Applicant Email Address',
            validate: applicantEmailValidate,
            events: {
              onBlur: ({ arrayIndex, valid, getValues }) => {
                const index = arrayIndex ?? 0;
                const applicants = (getValues('applicants') as Array<{ email?: string }>) ?? [];
                const applicantEmails = applicants.map(({ email }) => ({ value: email ?? '' }));
                trackInvite(InviteLeaseDetailsSegmentEvents.EmailValidationCompleted, {
                  field_name: `applicantEmails.${index}.value`,
                  applicant_index: index,
                  validation_result: getApplicantEmailValidationResult(
                    applicantEmails,
                    index,
                    valid,
                  ),
                  validation_source: 'client',
                });
              },
            },
          },
        },
      },
      screening: buildInviteScreeningSection(deps),
      coverage: buildInviteCoverageSection(deps),
    },
    submit: {
      buildPayload: (values) => {
        const renter = mapInviteFormValuesToRenter(values as InviteLeaseDetailsFormValues, {
          isSmbInvite,
          isPmi,
        });
        return { ...renter, selectedSmbPricing: selectedSmbPricingRef.current };
      },
      submit: (payload, ctx) => {
        onMilestoneReached?.('hadSubmitAttempt');
        // The submit pipeline (controller + `applyInviteServiceFieldErrors`) is
        // shared with the legacy form, which stores applicants at
        // `applicantEmails.X.value`. The shared engine stores them at
        // `applicants.X.email`. Translate legacy applicant paths to engine
        // paths so server-side per-applicant errors land on the right field;
        // everything else in `API_TO_FORM_FIELD` already uses engine-matching
        // names (leaseStartDate, unit, screeningStatus, etc.).
        const adaptedSetError: UseFormSetError<Renter> = (name, error, options) => {
          const enginePath = legacyApplicantPathToEngine(String(name));
          ctx.setError(enginePath, error, options);
        };
        return onSubmit(payload as Renter, adaptedSetError, ctx.attemptNumber);
      },
    },
    ui: {
      hideDefaultActions: true,
      inputSize: 'small',
      sectionDivider: <InviteLeaseDetailsFormDivider />,
      slots,
    },
  };
};
